import { useState } from 'react';
import { Box, Collapse, Divider, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

// ─── Category icon colours — cycles through brand palette ─────────────────────
const ACCENT_COLORS = [
  { bg: 'rgba(191,29,45,0.08)',  fg: '#BF1D2D' },  // brand red
  { bg: 'rgba(1,120,79,0.08)',   fg: '#01784F' },   // brand green
  { bg: 'rgba(20,62,246,0.08)',  fg: '#143EF6' },   // brand blue
  { bg: 'rgba(107,114,128,0.08)',fg: '#374151' },   // neutral
  { bg: 'rgba(191,29,45,0.08)',  fg: '#BF1D2D' },
  { bg: 'rgba(1,120,79,0.08)',   fg: '#01784F' },
];

// ─── Thin left-border accent on category header ───────────────────────────────
const BORDER_COLORS = ['#BF1D2D', '#01784F', '#143EF6', '#6B7280', '#BF1D2D', '#01784F'];

/**
 * ReportCategorySection
 *
 * Renders a single collapsible category on the Reports landing page.
 */
const ReportCategorySection = ({
  label,
  subtitle,
  reports,
  availableIds,
  onNavigate,
  open,
  onToggle,
  categoryIndex = 0,
}) => {

  const validReports = reports.filter(Boolean);
  const availableCount = validReports.filter((r) =>
    availableIds.includes(r.id)
  ).length;

  const accent = ACCENT_COLORS[categoryIndex % ACCENT_COLORS.length];
  const borderColor = BORDER_COLORS[categoryIndex % BORDER_COLORS.length];

  return (
    <Box
      sx={{
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #EAECF0',
        boxShadow: open
          ? '0 4px 16px rgba(0,0,0,0.06)'
          : '0 1px 3px rgba(0,0,0,0.04)',
        transition: 'box-shadow 0.2s ease',
      }}
    >
      {/* ── Category header ───────────────────────────────────────────────── */}
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          cursor: 'pointer',
          backgroundColor: open ? '#F9FAFB' : '#ffffff',
          borderLeft: `3px solid ${open ? borderColor : 'transparent'}`,
          userSelect: 'none',
          transition: 'background-color 0.15s ease, border-left-color 0.15s ease',
          '&:hover': {
            backgroundColor: '#F9FAFB',
            borderLeftColor: borderColor,
          },
        }}
      >
        {/* Left: icon + label */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Category icon dot */}
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '9px',
              backgroundColor: accent.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'transform 0.15s ease',
              ...(open && { transform: 'scale(1.05)' }),
            }}
          >
            {open ? (
              <ExpandMoreIcon sx={{ color: accent.fg, fontSize: 18 }} />
            ) : (
              <ChevronRightIcon sx={{ color: accent.fg, fontSize: 18 }} />
            )}
          </Box>

          {/* Label + subtitle */}
          <Box>
            <Typography
              sx={{
                fontFamily: 'karla, sans-serif',
                fontWeight: 700,
                fontSize: '15px',
                color: '#111827',
                lineHeight: 1.3,
              }}
            >
              {label}
            </Typography>
            {subtitle && (
              <Typography
                sx={{
                  fontFamily: 'karla, sans-serif',
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#9CA3AF',
                  lineHeight: 1.2,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right: availability badge */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.4,
              borderRadius: '20px',
              backgroundColor: availableCount > 0
                ? 'rgba(1,120,79,0.08)'
                : 'rgba(107,114,128,0.08)',
              border: `1px solid ${availableCount > 0 ? 'rgba(1,120,79,0.2)' : 'rgba(107,114,128,0.2)'}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: 'karla, sans-serif',
                color: availableCount > 0 ? '#01784F' : '#6B7280',
                whiteSpace: 'nowrap',
              }}
            >
              {availableCount} / {validReports.length}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Report rows ───────────────────────────────────────────────────── */}
      <Collapse in={open} timeout={200}>
        <Box sx={{ backgroundColor: '#ffffff' }}>
          {validReports.reduce((acc, report) => {
            const isAvailable = availableIds.includes(report.id);

            if (report.departmentSpecific && !isAvailable) return acc;

            const isFirst = acc.length === 0;
            acc.push(
              <Box key={report.id}>
                {!isFirst && <Divider sx={{ mx: 3, borderColor: '#F3F4F6' }} />}
                <ReportRow
                  report={report}
                  isAvailable={isAvailable}
                  onNavigate={onNavigate}
                  accentColor={accent.fg}
                />
              </Box>
            );
            return acc;
          }, [])}
        </Box>
      </Collapse>
    </Box>
  );
};

// ─── ReportRow ────────────────────────────────────────────────────────────────

const ReportRow = ({ report, isAvailable, onNavigate, accentColor }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onClick={() => isAvailable && onNavigate(report.link)}
      onMouseEnter={() => isAvailable && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 3,
        py: 1.75,
        pl: '68px', // aligns with label text above (36px icon + 16px gap + 16px px)
        backgroundColor: hovered ? '#F9FAFB' : '#ffffff',
        cursor: isAvailable ? 'pointer' : 'default',
        transition: 'background-color 0.12s ease',
        gap: 2,
      }}
    >
      {/* Left: name + description */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: 'karla, sans-serif',
            fontWeight: isAvailable ? 600 : 400,
            fontSize: '14px',
            color: isAvailable ? '#111827' : '#9CA3AF',
            lineHeight: 1.3,
          }}
        >
          {report.title}
        </Typography>
        {report.description && (
          <Typography
            sx={{
              fontFamily: 'karla, sans-serif',
              fontSize: '12px',
              color: isAvailable ? '#6B7280' : '#D1D5DB',
              mt: 0.3,
              lineHeight: 1.4,
            }}
          >
            {report.description}
          </Typography>
        )}
      </Box>

      {/* Right: action indicator */}
      {isAvailable ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.15s ease',
            color: accentColor,
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'karla, sans-serif',
              fontSize: '12px',
              fontWeight: 600,
              color: accentColor,
            }}
          >
            Open
          </Typography>
          <OpenInNewIcon sx={{ fontSize: 14, color: accentColor }} />
        </Box>
      ) : (
        <LockOutlinedIcon sx={{ fontSize: 15, color: '#D1D5DB', flexShrink: 0 }} />
      )}
    </Box>
  );
};

export default ReportCategorySection;
